import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { CheckCircle, Eye, X, ChevronLeft, ChevronRight } from 'lucide-react';

export const InstallationsSection = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const installations = [
    // Une seule présentation: système complet avec batterie et onduleur
    {
      id: 1,
      title: "Installation Domestique – Système complet batterie + onduleur",
      description: "Système PowMr complet avec batterie lithium et onduleur hybride",
      image: "/assets/installation-example.jpeg",
      type: "Domestique",
      location: "Bamako, Mali",
      power: "10kW"
    }
  ];

  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setSelectedImage(installations[index]);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const nextImage = () => {
    const nextIndex = (currentImageIndex + 1) % installations.length;
    setCurrentImageIndex(nextIndex);
    setSelectedImage(installations[nextIndex]);
  };

  const prevImage = () => {
    const prevIndex = (currentImageIndex - 1 + installations.length) % installations.length;
    setCurrentImageIndex(prevIndex);
    setSelectedImage(installations[prevIndex]);
  };

  const scrollToContact = () => {
    document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <section id="installations" className="py-20 bg-gradient-to-br from-gray-50 to-yellow-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-gradient-to-r from-yellow-100 to-yellow-200 text-gray-800 px-4 py-2 border border-yellow-300">
              Nos Réalisations
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Installations PowMr
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-yellow-600 to-gray-700">
                Par Nos Experts
              </span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Découvrez quelques-unes de nos installations PowMr réalisées au Mali. 
              <strong className="text-yellow-600">En tant que distributeur exclusif</strong>, nous garantissons 
              des installations professionnelles et conformes aux standards PowMr.
            </p>
          </div>

          {/* Installations Grid - Single category Domestique */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {installations.map((installation, index) => (
              <Card key={installation.id} className="group hover:shadow-xl transition-all duration-300 border-0 shadow-md hover:scale-105 bg-white overflow-hidden">
                <div className="relative">
                  <img 
                    src={installation.image} 
                    alt={installation.title}
                    className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center">
                    <Button
                      onClick={() => openLightbox(index)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white text-gray-900 hover:bg-gray-100"
                      size="sm"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Voir détails
                    </Button>
                  </div>
                  <Badge className="absolute top-3 left-3 bg-yellow-500 text-black text-xs">
                    Domestique
                  </Badge>
                </div>
                
                <CardContent className="p-4">
                  <h3 className="font-bold text-gray-900 mb-2 text-sm">
                    {installation.title}
                  </h3>
                  <p className="text-gray-600 text-xs mb-3 line-clamp-2">
                    {installation.description}
                  </p>
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-500">Localisation:</span>
                      <span className="font-medium text-gray-700">{installation.location}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-500">Puissance:</span>
                      <span className="font-medium text-yellow-600">{installation.power}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Call to Action */}
          <div className="text-center bg-white rounded-2xl p-8 md:p-12 shadow-lg border border-yellow-200">
            <div className="max-w-2xl mx-auto">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Votre Projet Mérite Une Installation Professionnelle
              </h3>
              <p className="text-gray-600 mb-8">
                Nos techniciens certifiés PowMr vous garantissent une installation conforme aux normes, 
                avec mise en service complète et formation à l'utilisation.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div className="flex items-center justify-center space-x-2 bg-gray-50 rounded-lg p-3">
                  <CheckCircle className="w-5 h-5 text-yellow-600" />
                  <span className="text-sm font-medium text-gray-700">Installation Certifiée</span>
                </div>
                <div className="flex items-center justify-center space-x-2 bg-gray-50 rounded-lg p-3">
                  <CheckCircle className="w-5 h-5 text-yellow-600" />
                  <span className="text-sm font-medium text-gray-700">Mise en Service</span>
                </div>
                <div className="flex items-center justify-center space-x-2 bg-gray-50 rounded-lg p-3">
                  <CheckCircle className="w-5 h-5 text-yellow-600" />
                  <span className="text-sm font-medium text-gray-700">Formation Incluse</span>
                </div>
              </div>

              <Button 
                onClick={scrollToContact}
                size="lg"
                className="bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black font-semibold px-8 py-4 text-lg rounded-xl transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl"
              >
                Demander une Installation
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4">
          <div className="relative max-w-4xl w-full">
            {/* Close button */}
            <Button
              onClick={closeLightbox}
              className="absolute top-4 right-4 z-10 bg-white text-black hover:bg-gray-100 rounded-full p-2"
              size="sm"
            >
              <X className="w-5 h-5" />
            </Button>

            {/* Navigation buttons */}
            <Button
              onClick={prevImage}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 bg-white text-black hover:bg-gray-100 rounded-full p-2"
              size="sm"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>

            <Button
              onClick={nextImage}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 bg-white text-black hover:bg-gray-100 rounded-full p-2"
              size="sm"
            >
              <ChevronRight className="w-5 h-5" />
            </Button>

            {/* Image and details */}
            <div className="bg-white rounded-lg overflow-hidden">
              <img 
                src={selectedImage.image} 
                alt={selectedImage.title}
                className="w-full h-96 object-cover"
              />
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-gray-900">{selectedImage.title}</h3>
                  <Badge className="bg-yellow-500 text-black">
                    {selectedImage.type}
                  </Badge>
                </div>
                <p className="text-gray-600 mb-4">{selectedImage.description}</p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Localisation:</span>
                    <span className="ml-2 font-medium text-gray-700">{selectedImage.location}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Puissance:</span>
                    <span className="ml-2 font-medium text-yellow-600">{selectedImage.power}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Image counter */}
            <div className="text-center mt-4">
              <span className="text-white text-sm">
                {currentImageIndex + 1} / {installations.length}
              </span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};